/**
 * aminos-slideshow.js
 *
 * Per-section initialization for the AA - Image + content slideshow.
 *
 * What it does:
 *   - Arrow buttons → scroll viewport by one slide width
 *   - Dot buttons   → scroll viewport to that index
 *   - Active dot is driven by IntersectionObserver on the slides,
 *     so native swipe + arrow + dot navigation all stay in sync
 *
 * Touch swipe is handled natively by CSS scroll-snap — no pointer/touch
 * event listeners needed.
 *
 * Reinitializes on the Shopify section editor's section:load event so
 * sections re-added in the customizer work without a page reload.
 */
(function () {
  'use strict';

  function initSlideshow(root) {
    if (root.__aminosSlideshowInit) return;
    root.__aminosSlideshowInit = true;

    var viewport = root.querySelector('[data-aminos-slideshow-viewport]');
    if (!viewport) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-aminos-slideshow-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-aminos-slideshow-dot]'));
    var prevBtns = root.querySelectorAll('[data-aminos-slideshow-prev]');
    var nextBtns = root.querySelectorAll('[data-aminos-slideshow-next]');

    function scrollToIndex(index) {
      if (index < 0) index = 0;
      if (index > slides.length - 1) index = slides.length - 1;
      var target = slides[index];
      if (!target) return;
      viewport.scrollTo({ left: target.offsetLeft - viewport.offsetLeft, behavior: 'smooth' });
    }

    function currentIndex() {
      var scrollLeft = viewport.scrollLeft;
      var width = viewport.clientWidth;
      // The slide whose left edge is closest to the current scrollLeft
      var nearest = 0;
      var nearestDist = Infinity;
      slides.forEach(function (slide, i) {
        var dist = Math.abs(slide.offsetLeft - viewport.offsetLeft - scrollLeft);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = i;
        }
      });
      return nearest;
    }

    prevBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        scrollToIndex(currentIndex() - 1);
      });
    });

    nextBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        scrollToIndex(currentIndex() + 1);
      });
    });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        var idx = parseInt(dot.getAttribute('data-index'), 10);
        if (isNaN(idx)) return;
        scrollToIndex(idx);
      });
    });

    function setActive(index) {
      dots.forEach(function (dot, i) {
        var isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      // Disable arrows at the ends
      prevBtns.forEach(function (b) { b.disabled = (index <= 0); });
      nextBtns.forEach(function (b) { b.disabled = (index >= slides.length - 1); });
    }

    // Track scroll position via IntersectionObserver — fires when each
    // slide enters/leaves the viewport. We pick the slide with the
    // largest intersection ratio.
    if ('IntersectionObserver' in window) {
      var ratios = new Array(slides.length).fill(0);
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var i = slides.indexOf(entry.target);
          if (i !== -1) ratios[i] = entry.intersectionRatio;
        });
        var best = 0;
        var bestRatio = -1;
        for (var i = 0; i < ratios.length; i++) {
          if (ratios[i] > bestRatio) {
            bestRatio = ratios[i];
            best = i;
          }
        }
        setActive(best);
      }, { root: viewport, threshold: [0.25, 0.5, 0.75, 1.0] });
      slides.forEach(function (slide) { io.observe(slide); });
    }

    setActive(0);
  }

  function initAll() {
    document.querySelectorAll('[data-aminos-slideshow]').forEach(initSlideshow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when a section is added/edited in the theme customizer
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-aminos-slideshow]');
    if (root) initSlideshow(root);
  });
})();
